// Low Stock drill-down — opens from the Dashboard "Low Stock Items" KPI tile.
// Lists every item below its configured reorder_level with shortfall qty and
// quick links to create a Purchase Order or update the master.
import { useEffect, useState, useMemo } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { useTabs } from "@/contexts/TabsContext";
import { RefreshCw, AlertTriangle, ShoppingCart, Package } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useClientPagination } from "@/hooks/useClientPagination";
import { PaginationControls } from "@/components/PaginationControls";

const INR = (n) => formatINR(n);
const NUM = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 3 }).format(Number(n) || 0);

export default function LowStockPage() {
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const { openTab } = useTabs();

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (branchId) params.set("branch_id", branchId);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const { data } = await api.get(`/inventory/low-stock${qs}`);
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load low-stock list");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchId]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      (r.item_code || "").toLowerCase().includes(needle) ||
      (r.item_name || "").toLowerCase().includes(needle) ||
      (r.category || "").toLowerCase().includes(needle)
    );
  }, [rows, q]);

  const totalShortfallValue = useMemo(
    () => filtered.reduce((s, r) => s + (Number(r.shortfall) * Number(r.last_purchase_rate || 0)), 0),
    [filtered]
  );

  const { paged, page, pageSize, total, setPage, setPageSize } = useClientPagination(filtered, { initialPageSize: 50 });

  return (
    <div data-testid="low-stock-page">
      <Toaster />
      <PageHeader
        eyebrow="Inventory Alerts"
        title="Low Stock — Items Below Reorder Level"
        subtitle="Items whose available quantity has dropped below the configured reorder level. Worst shortfalls first."
        actions={
          <div className="flex items-center gap-2">
            <select
              data-testid="low-stock-branch"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              className="border border-slate-300 px-2 py-1.5 text-xs"
            >
              <option value="">All Branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.branch_name}</option>
              ))}
            </select>
            <input
              data-testid="low-stock-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search code / name / category…"
              className="border border-slate-300 px-2 py-1.5 text-xs w-64"
            />
            <button
              data-testid="low-stock-refresh"
              onClick={load}
              className="bg-slate-100 hover:bg-slate-200 px-3 py-1.5 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
            >
              <RefreshCw size={11} /> Refresh
            </button>
          </div>
        }
      />

      {/* Summary strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <SummaryTile
          label="Items Below Reorder Level"
          value={filtered.length}
          icon={AlertTriangle}
          tone="rose"
          testid="low-stock-count"
        />
        <SummaryTile
          label="Total Shortfall Qty"
          value={NUM(filtered.reduce((s, r) => s + Number(r.shortfall || 0), 0))}
          icon={Package}
          tone="amber"
          testid="low-stock-qty"
        />
        <SummaryTile
          label="Est. Replenishment Value"
          value={INR(totalShortfallValue)}
          icon={ShoppingCart}
          tone="navy"
          testid="low-stock-value"
        />
      </div>

      {/* Data table */}
      <div className="bg-white border border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="low-stock-table">
            <thead className="bg-slate-50">
              <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2 text-left font-bold">Item Code</th>
                <th className="px-3 py-2 text-left font-bold">Item Name</th>
                <th className="px-3 py-2 text-left font-bold">Category</th>
                <th className="px-3 py-2 text-left font-bold">Unit</th>
                <th className="px-3 py-2 text-right font-bold">Reorder Level</th>
                <th className="px-3 py-2 text-right font-bold">Current Stock</th>
                <th className="px-3 py-2 text-right font-bold text-rose-700">Shortfall</th>
                <th className="px-3 py-2 text-right font-bold">Last Rate</th>
                <th className="px-3 py-2 text-center font-bold">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center text-slate-400 py-10">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="text-center text-slate-400 py-10" data-testid="low-stock-empty">
                  No items below reorder level. {rows.length === 0 ? "Configure a reorder level on the Items master to enable this alert." : "Nothing matches your search."}
                </td></tr>
              ) : (
                paged.map((r) => (
                  <tr key={r.item_id} className="border-t border-slate-100 hover:bg-rose-50/30" data-testid={`low-stock-row-${r.item_id}`}>
                    <td className="px-3 py-2 font-mono text-[11px]">{r.item_code}</td>
                    <td className="px-3 py-2 font-bold text-slate-800">{r.item_name}</td>
                    <td className="px-3 py-2 text-slate-600">{r.category}</td>
                    <td className="px-3 py-2 text-slate-600">{r.unit}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{NUM(r.reorder_level)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{NUM(r.current_stock)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-rose-700">{NUM(r.shortfall)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{INR(r.last_purchase_rate)}</td>
                    <td className="px-3 py-2 text-center">
                      <button
                        data-testid={`low-stock-po-${r.item_id}`}
                        onClick={() => openTab("/purchase-orders")}
                        className="text-[10px] uppercase tracking-wider font-bold text-[#F97316] hover:underline"
                      >
                        Raise PO →
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <PaginationControls page={page} pageSize={pageSize} total={total}
            onPage={setPage} onPageSize={setPageSize}
            pageSizeOptions={[25, 50, 100, 200]} />
        )}
      </div>
    </div>
  );
}

function SummaryTile({ label, value, icon: Icon, tone, testid }) {
  const palette = {
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    navy: "bg-[#0B1E3F] border-[#0B1E3F] text-white",
  }[tone] || "bg-white border-slate-200 text-slate-900";
  return (
    <div className={`border ${palette} p-3 flex items-center justify-between`} data-testid={testid}>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">{label}</div>
        <div className="font-display text-2xl font-black mt-1 tabular-nums">{value}</div>
      </div>
      <Icon size={28} className="opacity-60" />
    </div>
  );
}
